import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { forwardRef, useMemo, useState } from 'react';
import { SearchIcon, DownloadIcon } from '@primer/octicons-react';
import { LTInputBox, LTTooltip, LTUnderlineNav } from '@lambdatestincprivate/lt-components';
// Fixed fork — the lib drawer goes blank when opened <300ms after mount
// (stale close-timer race), which the palette's cross-tab jump does.
import { LTDrawer } from '../common/LTDrawerFixed';
import { mediaShort, uniqueSources } from '../../model/coverage';
import { readPackBytes } from '../../data/pack-store';
import { truncateMiddle } from '../format';
import styles from './coverage.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function sourceGlyph(mediaType) {
  if (mediaType === 'application/pdf') return '📕';
  if (mediaType === 'text/markdown') return '📄';
  return '📎';
}
function download(_x) {
  return _download.apply(this, arguments);
}
function _download() {
  _download = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(src) {
    var bytes, url, a;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          if (src.file) {
            _context.next = 1;
            break;
          }
          return _context.abrupt("return");
        case 1:
          _context.next = 2;
          return readPackBytes(src.file)["catch"](function () {
            return null;
          });
        case 2:
          bytes = _context.sent;
          if (bytes) {
            _context.next = 3;
            break;
          }
          return _context.abrupt("return");
        case 3:
          url = URL.createObjectURL(new Blob([bytes]));
          a = document.createElement('a');
          a.href = url;
          a.download = src.id;
          a.click();
          setTimeout(function () {
            return URL.revokeObjectURL(url);
          }, 1000);
        case 4:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _download.apply(this, arguments);
}
function SourceCard(_ref) {
  var src = _ref.src;
  var meta = mediaShort(src.mediaType);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.srcCard,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.srcCardTop,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.srcCardIcon,
        "aria-hidden": "true",
        children: sourceGlyph(src.mediaType)
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.srcCardText,
        children: [/*#__PURE__*/_jsxs("span", {
          className: styles.srcCardName,
          children: [src.id, src.stale && /*#__PURE__*/_jsx("span", {
            className: styles.srcStale,
            children: "stale"
          })]
        }), meta && /*#__PURE__*/_jsx("span", {
          className: styles.srcCardMeta,
          children: meta
        })]
      }), src.file && /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.srcCardDownload,
        "aria-label": "Download ".concat(src.id),
        onClick: function onClick() {
          return void download(src);
        },
        children: /*#__PURE__*/_jsx(DownloadIcon, {
          size: 16,
          "aria-hidden": "true"
        })
      })]
    }), src.usedIn.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: styles.srcCardUsed,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.srcCardUsedLabel,
        children: "Used in:"
      }), src.usedIn.map(function (ucId) {
        return /*#__PURE__*/_jsx(LTTooltip, {
          text: ucId,
          direction: "bottom",
          children: /*#__PURE__*/_jsx("span", {
            className: styles.idChip,
            children: truncateMiddle(ucId, 20)
          })
        }, ucId);
      })]
    })]
  });
}

/**
 * The Context drawer (Figma 30776-10530 entries): everything feeding the
 * run. Sources only for now — Clarification has no pack data yet and
 * renders as a disabled tab.
 */
export var ContextDrawer = /*#__PURE__*/forwardRef(function ContextDrawer(_ref2, ref) {
  var coverage = _ref2.coverage;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  var sources = useMemo(function () {
    return uniqueSources(coverage.usecases);
  }, [coverage]);
  var shown = sources.filter(function (s) {
    return s.id.toLowerCase().includes(query.trim().toLowerCase());
  });
  return /*#__PURE__*/_jsx(LTDrawer, {
    ref: ref,
    title: "Context",
    subTitle: "Everything feeding this run \u2014 used to generate the business use cases",
    position: "right"
    // Must be a CSS length — the lib injects it verbatim; a bare
    // number is dropped and the drawer goes content-sized (width jumped
    // while filtering).
    ,
    width: "560px",
    closable: true
    // Portals to the body — restate the viewer font on both regions.
    ,
    headerStyle: {
      fontFamily: 'var(--ev-font)'
    },
    bodyStyle: {
      fontFamily: 'var(--ev-font)',
      padding: 0
    },
    content: /*#__PURE__*/_jsxs("div", {
      className: styles.ctxBody,
      children: [/*#__PURE__*/_jsx(LTUnderlineNav
      // The lib types omit ariaLabel and demand Primer's children
      // prop; the runtime reads the former and ignores the latter.
      , {
        ariaLabel: 'Context sections',
        children: null,
        variant: LTUnderlineNav.Variants.PRIMARY,
        selectedVariant: LTUnderlineNav.SelectedVariants.HEADING,
        styles: {
          paddingLeft: '16px',
          paddingRight: '16px'
        }
        // Clarification hidden for now — no pack data at all yet;
        // add it back beside Sources when the backend ships it.
        ,
        navs: [{
          id: 'ctx-sources',
          key: 'sources',
          label: 'Sources',
          counter: sources.length,
          isSelected: true
        }],
        onClick: function onClick() {
          return undefined;
        }
      }), /*#__PURE__*/_jsx("div", {
        className: styles.ctxSearch,
        children: /*#__PURE__*/_jsx(LTInputBox, {
          inputOnly: true,
          size: "medium",
          placeholder: "Search sources",
          leadingIcon: SearchIcon,
          value: query,
          onChange: function onChange(value) {
            return setQuery(value);
          }
          // The lib's clear button only resets its internal text — it
          // never fires onChange; sync our state through it.
          ,
          trailingAction: function trailingAction() {
            return setQuery('');
          },
          "aria-label": "Search sources"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: styles.ctxList,
        children: shown.length === 0 ? /*#__PURE__*/_jsx("span", {
          className: styles.ctxEmpty,
          children: "No sources match."
        }) : shown.map(function (src) {
          return /*#__PURE__*/_jsx(SourceCard, {
            src: src
          }, src.id);
        })
      })]
    })
  });
});